import { useCallback, useReducer, useRef } from "react";
import { reportUiError, toUiError, type UiError } from "../../errors/uiError";
import { defineStateMachine } from "./stateMachine";

/** A form or action that sends something once per user request. */
export type SubmissionState =
  | { readonly status: "idle" }
  | { readonly status: "submitting" }
  | { readonly status: "succeeded" }
  | { readonly status: "failed"; readonly error: UiError };

type SubmissionEvent =
  | { readonly type: "started" }
  | { readonly type: "succeeded" }
  | { readonly type: "failed"; readonly error: UiError }
  | { readonly type: "reset" };

const started = (): SubmissionState => ({ status: "submitting" });

export const transitionSubmission = defineStateMachine<SubmissionState, SubmissionEvent>("submission", {
  idle: { started },
  submitting: {
    succeeded: () => ({ status: "succeeded" }),
    failed: (_state, event) => ({ status: "failed", error: event.error }),
  },
  succeeded: { started },
  failed: { started, reset: () => ({ status: "idle" }) },
});

export interface SubmissionHandle {
  readonly state: SubmissionState;
  /** Runs `action` unless a submission is already running. */
  readonly submit: (action: () => Promise<void>) => void;
  /** `submit` for a caller that waits on the outcome: whether `action` ran and succeeded. */
  readonly run: (action: () => Promise<void>) => Promise<boolean>;
  /** Forget a failure once what was sent has changed, so its error no longer shows. */
  readonly reset: () => void;
}

export function useSubmission(): SubmissionHandle {
  const [state, dispatch] = useReducer(transitionSubmission, { status: "idle" });
  // Guards against a second start before React has rendered "submitting", for example a
  // double click or an effect that runs twice in development.
  const runningRef = useRef(false);

  const run = useCallback((action: () => Promise<void>) => {
    if (runningRef.current) {
      return Promise.resolve(false);
    }
    runningRef.current = true;
    dispatch({ type: "started" });
    return action().then(
      () => {
        runningRef.current = false;
        dispatch({ type: "succeeded" });
        return true;
      },
      (caught: unknown) => {
        runningRef.current = false;
        const error = toUiError(caught);
        reportUiError(error);
        dispatch({ type: "failed", error });
        return false;
      },
    );
  }, []);
  const submit = useCallback((action: () => Promise<void>) => { void run(action); }, [run]);

  const reset = useCallback(() => {
    dispatch({ type: "reset" });
  }, []);

  return { state, submit, run, reset };
}
